import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router';
import { useQuery, useLazyQuery } from '@apollo/client';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';
import Chip from '@mui/joy/Chip';
import Alert from '@mui/joy/Alert';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Stars from '@mui/icons-material/Stars';
import Warning from '@mui/icons-material/Warning';
import { GET_CLIENT, VERIFY_CHECKOUT_SESSION } from '../../gql/subscription.queries';

export default function CreditsSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const purchasedAmount = searchParams.get('amount');
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationFailed, setVerificationFailed] = useState(false);

  const { data: clientData, refetch: refetchClient } = useQuery(GET_CLIENT, {
    fetchPolicy: 'network-only'
  });
  const [verifySession] = useLazyQuery(VERIFY_CHECKOUT_SESSION, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!sessionId) {
        await refetchClient();
        if (!cancelled) {
          setIsVerifying(false);
          setVerificationFailed(true);
        }
        return;
      }

      try {
        const { data } = await verifySession({ variables: { sessionId } });
        const session = data?.verifyCheckoutSession;

        if (session?.status === 'complete' && session?.paymentStatus === 'paid') {
          await refetchClient();
          if (!cancelled) setIsVerifying(false);
        } else if (session?.status === 'expired') {
          if (!cancelled) {
            setIsVerifying(false);
            setVerificationFailed(true);
          }
        } else {
          // Session still open/processing — poll a few times
          const maxRetries = 5;
          for (let i = 0; i < maxRetries; i++) {
            if (cancelled) return;
            await new Promise(resolve => setTimeout(resolve, 2000));
            const { data: retryData } = await verifySession({ variables: { sessionId } });
            const retrySession = retryData?.verifyCheckoutSession;
            if (retrySession?.status === 'complete' && retrySession?.paymentStatus === 'paid') {
              await refetchClient();
              if (!cancelled) setIsVerifying(false);
              return;
            }
          }
          if (!cancelled) {
            await refetchClient();
            setIsVerifying(false);
            setVerificationFailed(true);
          }
        }
      } catch {
        if (!cancelled) {
          await refetchClient();
          setIsVerifying(false);
          setVerificationFailed(true);
        }
      }
    };

    verify();

    return () => { cancelled = true; };
  }, [sessionId, verifySession, refetchClient]);

  const client = clientData?.getClient;

  if (isVerifying) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        gap: 2
      }}>
        <CircularProgress size="lg" />
        <Typography level="body-lg" color="neutral">
          Processing your purchase...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh',
      p: 2
    }}>
      <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
        <CardContent sx={{ gap: 3, alignItems: 'center' }}>
          <Box sx={{ position: 'relative' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.500' }} />
            <Stars 
              sx={{ 
                position: 'absolute', 
                top: -8, 
                right: -8, 
                fontSize: 32, 
                color: 'warning.500' 
              }} 
            />
          </Box>
          
          <Box>
            <Typography level="h2" sx={{ mb: 1 }}>
              Credits Added!
            </Typography>
            <Typography level="body-lg" color="neutral">
              Your credit purchase was successful.
            </Typography>
          </Box>

          {verificationFailed && (
            <Alert color="warning" variant="soft" startDecorator={<Warning />} sx={{ width: '100%', textAlign: 'left' }}>
              Your payment was received but processing is taking longer than usual. Your credits will appear shortly.
            </Alert>
          )}

          {purchasedAmount && (
            <Chip
              size="lg"
              variant="soft"
              color="success"
              sx={{ fontSize: 'lg', py: 1, px: 3 }}
            >
              +{purchasedAmount} credits
            </Chip>
          )}

          <Box sx={{ 
            bgcolor: 'background.level1', 
            p: 3, 
            borderRadius: 'md',
            width: '100%'
          }}>
            <Typography level="body-sm" color="neutral" sx={{ mb: 1 }}>
              Total Credits Available
            </Typography>
            <Typography level="h1" color="primary">
              {client?.credits ?? 0}
            </Typography>
            <Typography level="body-sm" color="neutral">
              / {client?.currentTier?.maxCreditsAllowed ?? 0} max
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              size="lg"
              component={Link}
              to="/jobs"
            >
              Go to Jobs
            </Button>
            <Button
              size="lg"
              variant="outlined"
              component={Link}
              to="/settings/billing"
            >
              View Billing
            </Button>
          </Box>

          {sessionId && (
            <Typography level="body-xs" color="neutral">
              Session ID: {sessionId}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
